import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';

import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch.gts';
import ToolTipComponent from 'webapp/components/tool-tip.gts';

type Args = Record<string, never>;

export default class BillingOnlyAdminsSettingsComponent extends Component<void> {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	@tracked model: APIData.Account.AccountUpdate;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.model = this.accountService.accountObject;
	}

	save(): void {
		const layer = this.infolayerService.loading('Saving restriction change...');

		this.accountService
			.updateAccountDataToBE(this.model)
			.then(
				() => {
					layer.ok('Restriction change saved!');
				},
				(errorData) => {
					layer.close();

					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	changeOnlyAdminsConnect(value: boolean): void {
		this.model.onlyAdminsConnect = value;
		debounce(this, this.save, 150);
	}

	@action
	changeOnlyAdminsCanBuyCredits(value: boolean): void {
		this.model.onlyAdminsCanBuyCredits = value;
		debounce(this, this.save, 150);
	}

	<template>
		<div class="admin-settings">
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="onlyAdminsConnect"
					@selected={{this.accountService.onlyAdminsConnect}}
					@onChange={{this.changeOnlyAdminsConnect}}
				/>
				<label>
					Restrict non-admin users from adding new data sources
					<ToolTipComponent
						@text="By selecting this setting, only admins (including the account owner) will be able to add new data sources."
					/>
				</label>
			</div>
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="onlyAdminsCanBuyCredits"
					@selected={{this.accountService.onlyAdminsCanBuyCredits}}
					@onChange={{this.changeOnlyAdminsCanBuyCredits}}
				/>
				<label>
					Restrict non-admin users from buying AI credit top-ups
					<ToolTipComponent
						@text="When enabled, only admins, including the account owner, can buy AI credit top-ups."
					/>
				</label>
			</div>
		</div>
	</template>
}
